import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import './index.scss';
import Editor  from '@monaco-editor/react';
import ModalButtons from "../ModalButtons";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExpandAlt, faCompressAlt } from '@fortawesome/free-solid-svg-icons';

const CustomJSModal = ({ customJSBlockCode, customJSBlockCodeChange, mode = 'customJS' }) => {
    const isMCJavaScript = mode === 'mcJavaScript';
    const argString = 'params';
    let init = isMCJavaScript ? `
/* MobiControl JavaScript
 * This code is passed to the MobiControl Agent as a plain script string and
 * executed as-is - it is NOT spliced into Blockly-generated code, so
 * SnapReturns and Blockly variable scoping do not apply here.
 *
 * Example:
 * mobicontrol.lockdown.enable();
 */

// Add your MC JavaScript below
` : `
/* Blockly Custom Script
 * Implemented in Javascript
 * To learn more about Javascript, 
 * please visit https://developer.mozilla.org/en-US/docs/Web/JavaScript/
 *
 *
 * Block Arguments: ${argString}
 * 
 * Any return values from this code can be stored in Blockly Local Variable
 * using \`SnapReturns\` keyword as follows:
 * SnapReturns.result = "Hello World!"
 * variable result containing the assigned value can be found in
 * the local variables blocks.
 * 
 */
 
// Add your custom script below
`;
    const initVal = customJSBlockCode || init;
    const [value, setValue] = useState(initVal);
    const [editor, setEditor] = useState(null);
    const [codeEditor, setCodeEditor] = useState(null);
    const [isEditorFocused, setIsEditorFocused] = useState(false);
    const [expanded, setExpanded] = useState(false);
    const [completionProvider, setCompletionProvider] = useState(null);

    const _sanitizeCode = code => {
        // Why we sanitize at all (unrelated to ES5/ES6 support):
        // CustomJSBlock.js splices this code directly into the surrounding generated
        // script with no per-block function wrapper (see
        // components/Toolbox/blockVersions/*/assistingBlocks/CustomJSBlock.js).
        // Block-scoped let/const would:
        //   (a) throw "already declared" if the user duplicates this block, or
        //   (b) fall out of scope if nested inside a control-flow block's braces.
        // So const/let must be downgraded to var here regardless of runtime ES6 support.
        //
        // NOSONAR (why the regex itself is not a ReDoS risk):
        // The single, non-nested `\s*` quantifier is followed by an unambiguous `\b`
        // boundary and a non-overlapping-prefix alternation (const|let), so there is
        // no ambiguous substring for the regex engine to backtrack over - matching
        // stays linear in input length.
        return code.replace(/^\s*\b(const|let)\b/gm, 'var'); // NOSONAR
    };

    const _handleEditorBeforeMount = monaco => {
        function createProposals(args) {
            return [
                ...args.map(arg => ({
                    label: arg,
                    kind: monaco.languages.CompletionItemKind.Variable,
                    insertText: arg
                })),
                {
                    label: "SnapReturns",
                    kind: monaco.languages.CompletionItemKind.Method,
                    insertText: "SnapReturns."
                }
            ];
        }

        const completionProvider = monaco.languages.registerCompletionItemProvider('javascript', {
            provideCompletionItems: function() {
                // params/SnapReturns are Custom-JS-only concepts; MC JavaScript has neither
                const suggestions = isMCJavaScript ? [] : createProposals([argString]);
                return {
                    suggestions: suggestions
                };
            }
        });
        setCompletionProvider(completionProvider);
        setEditor(monaco);
    };

    const _handleEditorMount = mountedEditor => {
        const model = mountedEditor.getModel();

        if (!customJSBlockCode && model) {
            const firstEditablePosition = {
                lineNumber: model.getLineCount(),
                column: 1
            };

            mountedEditor.setPosition(firstEditablePosition);
        }

        setCodeEditor(mountedEditor);
        mountedEditor.focus();
    };

    const _expandModal = () => {
        if (expanded) {
            setExpanded(false);
            let modalWrapper = document.getElementById("save-modal-wrapper");
            modalWrapper.className = "modal-wrapper";
        } else {
            setExpanded(true);
            let modalWrapper = document.getElementById("save-modal-wrapper");
            modalWrapper.className = "modal-wrapper-expanded";
        }
    }

    const _handleExpandModalKeyDown = event => {
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            _expandModal();
        }
    }

    useEffect(() => {
        return function cleanup() {
            editor?.editor.getModels().map(model => model.dispose());
        };
    }, [editor]);

    useEffect(() => {
        return function cleanup() {
            completionProvider?.dispose()
        }
    }, [completionProvider]);

    useEffect(() => {
        if (!codeEditor) {
            return;
        }

        const updateEditorFocus = () => {
            setIsEditorFocused(document.hasFocus() && codeEditor.hasTextFocus());
        };
        const handleWindowBlur = () => setIsEditorFocused(false);
        const editorFocusSubscription = codeEditor.onDidFocusEditorText(updateEditorFocus);
        const editorBlurSubscription = codeEditor.onDidBlurEditorText(updateEditorFocus);

        window.addEventListener('focus', updateEditorFocus);
        window.addEventListener('blur', handleWindowBlur);
        updateEditorFocus();

        return function cleanup() {
            window.removeEventListener('focus', updateEditorFocus);
            window.removeEventListener('blur', handleWindowBlur);
            editorFocusSubscription.dispose();
            editorBlurSubscription.dispose();
        };
    }, [codeEditor]);

    return (
        <div id={"save-modal-container"}>
            <div className={"modal-wrapper"} id={"save-modal-wrapper"}>
                <div className={"modal-header small"}>
                    <h4>{isMCJavaScript ? 'Execute MC JavaScript' : 'Create Custom JS Code Block'}</h4>
                    {/* Kept as a div (rather than <button>) to preserve existing layout/styling;
                        role/tabIndex/onKeyDown below provide equivalent keyboard/AT accessibility. */}
                    <div // NOSONAR - intentionally interactive div, not a native button; see comment above
                        className={"expand-icon-container"}
                        role={"button"}
                        tabIndex={0}
                        aria-label={expanded ? "Collapse modal" : "Expand modal"}
                        onClick={_expandModal}
                        onKeyDown={_handleExpandModalKeyDown}
                    >
                        { expanded ?
                            <FontAwesomeIcon icon={faCompressAlt} /> :
                            <FontAwesomeIcon icon={faExpandAlt} />
                        }
                    </div>
                </div>
                <div id={"scriptArea"} className={`modal-body${isEditorFocused ? ' script-area--focused' : ''}`}>
                    <Editor
                        language="javascript"
                        value={value}
                        options={{filteredTypes: { interface: false }}}
                        beforeMount={_handleEditorBeforeMount}
                        onMount={_handleEditorMount}
                        onChange={val => {setValue(val)}}
                        theme={document.body.classList.contains('darkTheme') ? 'vs-dark' : 'vs'}/>
                    {
                        codeEditor && !isEditorFocused
                            ? <output className="editor-focus-hint">Editor is inactive. Click inside to type or paste.</output>
                            : null
                    }
                </div>
                <div className="modal-footer">
                    <ModalButtons next={() => customJSBlockCodeChange(isMCJavaScript ? value : _sanitizeCode(value))} />
                </div>
            </div>
        </div>
    );
};

CustomJSModal.propTypes = {
    customJSBlockCode: PropTypes.string,
    customJSBlockCodeChange: PropTypes.func.isRequired,
    mode: PropTypes.oneOf(['customJS', 'mcJavaScript'])
};

export default withRouter(CustomJSModal);